import { useCallback, useEffect, useRef, useState } from "react";
import { api, errMsg, wsUrl } from "@/lib/api";

export function useLiveStandings(slug, filters) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [conn, setConn] = useState("connecting");
  const [received, setReceived] = useState(null);
  const key = JSON.stringify(filters || {});
  const keyRef = useRef(key);
  keyRef.current = key;

  const load = useCallback(async () => {
    if (!slug) return;
    try {
      const { data: d } = await api.get(`/events/${slug}/standings`, { params: JSON.parse(keyRef.current) });
      setData(d);
      setError(null);
      setReceived(new Date());
    } catch (e) {
      setError(errMsg(e));
    }
  }, [slug]);

  useEffect(() => {
    setData(null);
    load();
  }, [load, key]);

  const eventId = data?.event?.id;
  useEffect(() => {
    if (!eventId) {
      return undefined;
    }
    let ws;
    let timer;
    let closed = false;
    let attempt = 0;
    const connect = () => {
      ws = new WebSocket(wsUrl(`/ws/events/${eventId}`));
      const sock = ws;
      const connectTimer = setTimeout(() => { if (sock.readyState !== 1) sock.close(); }, 8000);
      ws.onopen = () => { clearTimeout(connectTimer); attempt = 0; setConn("live"); load(); };
      ws.onmessage = () => load();
      ws.onclose = () => {
        clearTimeout(connectTimer);
        if (closed) return;
        setConn("reconnecting");
        attempt += 1;
        timer = setTimeout(connect, Math.min(15000, 1000 * 2 ** attempt));
      };
      ws.onerror = () => ws.close();
    };
    connect();
    const ping = setInterval(() => { if (ws?.readyState === 1) ws.send("ping"); }, 25000);
    const poll = setInterval(() => { if (ws?.readyState !== 1) load(); }, 20000);
    return () => {
      closed = true;
      clearTimeout(timer);
      clearInterval(ping);
      clearInterval(poll);
      if (ws) ws.close();
    };
  }, [eventId, load]);

  return { data, error, conn, received, reload: load };
}
